import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { FolderTree, Plus, Trash2, UserPlus, UserMinus, X } from 'lucide-react';

interface GroupMember {
  userId: string;
  email: string;
  role: string;
  joinedAt: string;
}

interface Group {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  members: GroupMember[];
  createdAt: string;
}

interface SimpleUser {
  id: string;
  email: string;
}

export function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMember, setShowAddMember] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<SimpleUser[]>([]);

  const loadGroups = () => {
    api
      .get<{ groups: Group[] }>('/groups')
      .then((r) => setGroups(r.groups))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadGroups();
    // Alle Users laden für Mitglieder-Dialog
    api
      .get<{ accounts: SimpleUser[] }>('/groups/accounts')
      .then((r) => setAllUsers(r.accounts))
      .catch(() => {});
  }, []);

  const deleteGroup = async (id: string, name: string) => {
    if (!confirm(`Gruppe "${name}" wirklich löschen?`)) return;
    await api.delete(`/groups/${id}`);
    loadGroups();
  };

  const removeMember = async (groupId: string, userId: string) => {
    await api.delete(`/groups/${groupId}/members/${userId}`);
    loadGroups();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Gruppen</h1>
          <p className="text-sm text-slate-500 mt-1">
            Benutzergruppen verwalten
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} />
          Neue Gruppe
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Laden ...</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FolderTree size={40} className="mx-auto mb-3 text-slate-300" />
          <p>Noch keine Gruppen vorhanden</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {groups.map((g) => (
            <div key={g.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{g.name}</h3>
                  {g.description && (
                    <p className="text-sm text-slate-500 mt-0.5">{g.description}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {g.memberCount} Mitglied{g.memberCount !== 1 ? 'er' : ''} &middot;
                    Erstellt {new Date(g.createdAt).toLocaleDateString('de-DE')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddMember(g.id)}
                    className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
                    title="Mitglied hinzufügen"
                  >
                    <UserPlus size={16} />
                  </button>
                  <button
                    onClick={() => deleteGroup(g.id, g.name)}
                    className="p-2 rounded-md hover:bg-red-50 text-red-500"
                    title="Gruppe löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Mitglieder-Liste */}
              {g.members.length > 0 && (
                <div className="border-t border-slate-100 pt-3">
                  <div className="space-y-2">
                    {g.members.map((m) => (
                      <div
                        key={m.userId}
                        className="flex items-center justify-between text-sm py-1"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-rla-100 text-rla-700 flex items-center justify-center text-xs font-medium">
                            {m.email.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-slate-700">{m.email}</span>
                          {m.role === 'admin' && (
                            <span className="text-xs bg-rla-50 text-rla-700 px-1.5 py-0.5 rounded">
                              Admin
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => removeMember(g.id, m.userId)}
                          className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                          title="Entfernen"
                        >
                          <UserMinus size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog: Neue Gruppe */}
      {showCreate && (
        <CreateGroupDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadGroups();
          }}
          allUsers={allUsers}
        />
      )}

      {/* Dialog: Mitglied hinzufügen */}
      {showAddMember && (
        <AddMemberDialog
          groupId={showAddMember}
          existingMembers={groups.find((g) => g.id === showAddMember)?.members ?? []}
          allUsers={allUsers}
          onClose={() => setShowAddMember(null)}
          onAdded={() => {
            setShowAddMember(null);
            loadGroups();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dialog: Gruppe erstellen                                           */
/* ------------------------------------------------------------------ */

function CreateGroupDialog({
  onClose,
  onCreated,
  allUsers,
}: {
  onClose: () => void;
  onCreated: () => void;
  allUsers: SimpleUser[];
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/groups', {
        name,
        description: description || undefined,
        memberIds: selectedIds,
      });
      onCreated();
    } catch {
      setError('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Neue Gruppe</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={50}
              placeholder="z.B. Entwicklung"
            />
          </div>
          <div>
            <label className="label">Beschreibung (optional)</label>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              placeholder="Kurze Beschreibung"
            />
          </div>
          <div>
            <label className="label">Mitglieder auswählen</label>
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
              {allUsers.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(u.id)}
                    onChange={() => toggle(u.id)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-slate-700">{u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full">
            {saving ? 'Erstelle ...' : 'Gruppe erstellen'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dialog: Mitglied hinzufügen                                         */
/* ------------------------------------------------------------------ */

function AddMemberDialog({
  groupId,
  existingMembers,
  allUsers,
  onClose,
  onAdded,
}: {
  groupId: string;
  existingMembers: GroupMember[];
  allUsers: SimpleUser[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const existingIds = new Set(existingMembers.map((m) => m.userId));
  const available = allUsers.filter((u) => !existingIds.has(u.id));
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/groups/${groupId}/members`, { userIds: selectedIds });
      onAdded();
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Mitglieder hinzufügen</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {available.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              Alle Benutzer sind bereits Mitglied
            </p>
          ) : (
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
              {available.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(u.id)}
                    onChange={() => toggle(u.id)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-slate-700">{u.email}</span>
                </label>
              ))}
            </div>
          )}
          <button
            type="submit"
            disabled={saving || selectedIds.length === 0}
            className="btn-primary w-full"
          >
            {saving ? 'Hinzufügen ...' : `${selectedIds.length} hinzufügen`}
          </button>
        </form>
      </div>
    </div>
  );
}
